import { SuggestionData } from 'ckeditor5-premium-features'
import {
    getSuggestionCardsById,
    highlightCard,
} from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { useHighlightStoreShallowSelector } from 'components/WorkspaceEditor/HighlightStore'
import { ListRef } from 'rc-virtual-list'
import { useCallback, useEffect, useRef } from 'react'
/**
 * Custom hook to handle highlighting virtualized suggestion cards.
 * It scrolls to the card and highlights it when requested.
 *
 * @param {SuggestionData[]} suggestions - List of suggestion data.
 * @param {React.RefObject<ListRef>} containerRef - Reference to the rc-virtual-list container.
 * @param {Object} [retryOptions] - Options for retrying highlighting if card is not immediately available.
 * @param {number} [retryOptions.maxAttempts=10] - Maximum number of attempts to retry highlighting.
 * @param {number} [retryOptions.initialDelay=500] - Initial delay before retrying.
 * @param {number} [retryOptions.backoffFactor=2] - Factor by which the delay increases on each retry.
 * @returns {Function} Function to highlight a card by its ID and class name.
 */
export const useHighlightVirtualizedCard = (
    suggestions: SuggestionData[],
    containerRef: React.RefObject<ListRef>,
    retryOptions?: {
        maxAttempts?: number
        initialDelay?: number
        backoffFactor?: number
    }
) => {
    const {
        highlightRequest,
        clearHighlight,
    } = useHighlightStoreShallowSelector((state) => ({
        highlightRequest: state.highlightRequest,
        clearHighlight: state.clearHighlight,
    }))

    const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

    const getIndexById = useCallback(
        (suggestionId: string) =>
            suggestions.findIndex(
                (s) => s?.id === suggestionId || s?.data?.key === suggestionId
            ),
        [suggestions]
    )

    const { maxAttempts = 10, initialDelay = 800, backoffFactor = 2 } =
        retryOptions || {}

    const tryHighlightCard = useCallback(
        (id: string, className: string, attempt = 0, delay = initialDelay) => {
            const cards = getSuggestionCardsById(id)
            const card = cards?.[0]

            if (card) {
                card.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                })

                setTimeout(() => {
                    highlightCard(id, className, true)
                }, 100)

                return
            }

            if (attempt < maxAttempts) {
                const timeoutId = setTimeout(() => {
                    tryHighlightCard(
                        id,
                        className,
                        attempt + 1,
                        delay * backoffFactor
                    )
                }, delay)
                timeoutsRef.current.push(timeoutId)
            }
        },
        [maxAttempts, initialDelay, backoffFactor]
    )
    const handleHighlight = useCallback(
        (suggestionId: string, highlightClass: string) => {
            const index = getIndexById(suggestionId)
            if (index === -1) return

            containerRef.current?.scrollTo({
                index,
                align: 'auto',
            })

            tryHighlightCard(suggestionId, highlightClass)
        },
        [getIndexById, containerRef, tryHighlightCard]
    )

    useEffect(() => {
        if (highlightRequest?.id && highlightRequest.className) {
            handleHighlight(highlightRequest.id, highlightRequest.className)
            clearHighlight()
        }
    }, [highlightRequest, handleHighlight, clearHighlight])

    useEffect(() => {
        return () => {
            timeoutsRef.current.forEach(clearTimeout)
            timeoutsRef.current = []
        }
    }, [])

    return handleHighlight
}
